// Gun harmonization: where a fighter's battery converges, the pilot's call.
//
// Every airframe leaves the factory boresighted at one range
// (aircraftTypes[i].gun.convergenceM). The hangar's GUNS row lets a pilot
// pull that in for a knife fight or push it out for the long snap shot, one
// setting per airframe. The pilot's career keeps the settings (they ride the
// welcome as career.conv — adoptCareerConvergence), so they follow the pilot
// to any device; this device's copy is the offline hint that paints the hangar
// before the handshake. The server aims every barrel at
// the chosen range (harmonize.go — its clamp is the mirror of the one here)
// and the pipper and the boresight cross read convergenceFor(), so the sight
// always sits where the rounds meet.
//
// Settings are keyed by airframe NAME, not roster index: the hangar grows,
// and a Spitfire harmonized at 200 m should still be a Spitfire harmonized at
// 200 m after a new type slots in ahead of it.

import { batterySpan } from "./battery.js";

/** Closest harmonization on offer (m) — mirrors gunConvMinM in harmonize.go. */
export const CONV_MIN_M = 150;
/** Longest harmonization on offer (m) — mirrors gunConvMaxM in harmonize.go. */
export const CONV_MAX_M = 600;
/** The armorer's click: the slider and the ± buttons move in these steps. */
export const CONV_STEP_M = 25;

const STORE_KEY = "va-conv";
// Airframes re-dialled in the hangar before the server heard about it (picked
// before TAKE OFF, when there's no socket to tell): the next welcome must hand
// these over rather than let the career's older copy overwrite them.
const UNSENT_KEY = "va-conv-unsent";

let saved = null; // { [planeName]: meters } — lazily read from localStorage
let unsent = null; // Set of plane names — lazily read from localStorage

function store() {
  if (saved) return saved;
  saved = {};
  try {
    const raw = JSON.parse(localStorage.getItem(STORE_KEY) ?? "{}");
    if (raw && typeof raw === "object") {
      for (const [k, v] of Object.entries(raw)) {
        const m = Number(v);
        if (m > 0) saved[k] = clampConvergence(m);
      }
    }
  } catch {}
  return saved;
}

function unsentSet() {
  if (unsent) return unsent;
  unsent = new Set();
  try {
    const raw = JSON.parse(localStorage.getItem(UNSENT_KEY) ?? "[]");
    if (Array.isArray(raw)) for (const n of raw) if (typeof n === "string") unsent.add(n);
  } catch {}
  return unsent;
}

function saveUnsent() {
  try {
    localStorage.setItem(UNSENT_KEY, JSON.stringify([...unsentSet()]));
  } catch {}
}

/** Snap a range to the armorer's steps inside the offered band. */
export function clampConvergence(m) {
  const snapped = Math.round(m / CONV_STEP_M) * CONV_STEP_M;
  return Math.max(CONV_MIN_M, Math.min(CONV_MAX_M, snapped));
}

/** The factory boresight for an airframe (0 = it carries no fixed guns). */
export function factoryConvergence(type) {
  return type?.gun?.convergenceM ?? 0;
}

/** Where this airframe's guns converge for this pilot: their own setting, or the factory's. */
export function convergenceFor(type) {
  const factory = factoryConvergence(type);
  if (!(factory > 0)) return 0;
  return store()[type.name] ?? factory;
}

/** True when the pilot has re-harmonized this airframe away from the factory. */
export function isCustomConvergence(type) {
  return type?.name != null && store()[type.name] != null;
}

/**
 * Record a pilot's harmonization for an airframe. A range equal to the
 * factory's (or <= 0) forgets the setting, so a pilot who dials back to stock
 * is flying stock — and follows the factory if a balance pass moves it.
 * `sent` says whether the order went straight to the server; a pick made with
 * no socket (the hangar before TAKE OFF) is held for the next welcome, where
 * it outranks the career's copy. Returns the range the guns now converge at.
 */
export function setConvergence(type, meters, sent = false) {
  const s = store();
  const u = unsentSet();
  if (sent) u.delete(type.name);
  else u.add(type.name);
  saveUnsent();
  const factory = factoryConvergence(type);
  const m = meters > 0 ? clampConvergence(meters) : 0;
  if (!m || m === factory) delete s[type.name];
  else s[type.name] = m;
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(s));
  } catch {}
  return s[type.name] ?? factory;
}

/**
 * The wire orders that hand the server this device's harmonization, one per
 * re-harmonized airframe: [{ plane: index, cv: meters }].
 */
export function convergenceOrders(planeTypes) {
  const out = [];
  planeTypes.forEach((t, i) => {
    if (isCustomConvergence(t) && factoryConvergence(t) > 0) out.push({ plane: i, cv: convergenceFor(t) });
  });
  return out;
}

/**
 * Seed the device from the career the welcome carries (career.conv: airframe
 * name -> meters, 0 = the pilot put it back to stock) and return the orders
 * that still need sending. The career wins for every type it names — that's
 * the pilot's setting, whichever machine they dialled it on — except a type
 * re-dialled here since the last welcome and never sent, which is the newest
 * word of all. This device keeps the rest: a type the career has never heard
 * of (set here while offline, or before careers kept harmonization) is handed
 * over, and the server files it on the career for next time. Types another
 * theater's roster flies ride along in the career untouched.
 */
export function adoptCareerConvergence(career, planeTypes) {
  const conv = career?.conv;
  const known = conv && typeof conv === "object" ? conv : null;
  const u = unsentSet();
  if (known) {
    const s = store();
    const factories = new Map(planeTypes.map((t) => [t.name, factoryConvergence(t)]));
    for (const [name, v] of Object.entries(known)) {
      if (u.has(name)) continue;
      const m = Number(v) > 0 ? clampConvergence(Number(v)) : 0;
      if (!m || m === factories.get(name)) delete s[name];
      else s[name] = m;
    }
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(s));
    } catch {}
  }
  const out = [];
  planeTypes.forEach((t, i) => {
    if (!(factoryConvergence(t) > 0)) return;
    if (u.has(t.name)) out.push({ plane: i, cv: isCustomConvergence(t) ? convergenceFor(t) : 0 });
    else if (!(known && t.name in known) && isCustomConvergence(t)) out.push({ plane: i, cv: convergenceFor(t) });
  });
  for (const o of out) u.delete(planeTypes[o.plane].name);
  saveUnsent();
  return out;
}

/**
 * The battery's spanwise reach (m): its outermost barrel off the centerline
 * — a Spitfire's outboard .303, a Mustang's outer fifty — which is how far
 * apart the widest streams start. Mirrors the muzzles fireGun fires from
 * (battery.js; the legacy block's WingSpanM * 0.28 pair included).
 */
export function wingGunOffset(type) {
  return batterySpan(type);
}

/**
 * How far apart the two wing streams fly at a range (m), for guns converged
 * at conv: the straight-line toe-in, which is what a pilot sees on a target's
 * wings — zero at the harmonization, a full battery-span apart at the muzzle
 * and again at twice the range.
 */
export function streamGap(type, conv, rangeM) {
  const w = wingGunOffset(type);
  if (!(conv > 0)) return 2 * w;
  return 2 * w * Math.abs(1 - rangeM / conv);
}

/**
 * One line of armorer's advice for a harmonization — what it buys and what
 * it costs, in the cockpit's terms. Nose guns get their own line: their
 * stream already rides the centerline, so the range only sets where it
 * crosses the pipper.
 */
export function convergenceAdvice(type, m) {
  if (wingGunOffset(type) < 1) {
    return `NOSE GUN — the stream rides the centerline; this ranges the pipper to ${m} m.`;
  }
  const near = Math.round(streamGap(type, m, 100) * 10) / 10;
  const far = Math.round(streamGap(type, m, 500) * 10) / 10;
  if (m <= 200) return `KNIFE FIGHT — one fist at ${m} m. Close in before you press: at 500 m the streams fly ${far} m apart.`;
  if (m >= 400) return `LONG REACH — the volley meets at ${m} m for the snap shot. Up close it straddles: ${near} m apart at 100 m.`;
  return `THE CLASSIC BOX — the battery meets at ${m} m, where most scraps are won. ${near} m apart at 100 m, ${far} m at 500.`;
}

/**
 * The GUNS row's top-down sketch: the two wing streams toeing in from the
 * wingtips, crossing at the harmonization and fanning out beyond it, over a
 * range scale. Returns SVG markup sized viewBox 0 0 300 70 (the row's CSS
 * scales it); gold streams, a gold ring on the crossing, a ghost tick at the
 * factory setting when the pilot has moved off it.
 */
export function convergenceSketch(type, m, factory) {
  const x0 = 34;
  const x1 = 292;
  const maxR = 650;
  const X = (r) => x0 + ((x1 - x0) * r) / maxR;
  const mid = 30;
  const w = wingGunOffset(type);
  // Exaggerate the span so a 3 m battery reads on a 300 px sketch.
  const lat = w > 0 ? 14 : 0;
  const yAt = (side, r) => mid + side * lat * (1 - r / m);
  // Past the crossing a stream fans out; stop it at the sketch's edge (two
  // spans off the nose line) rather than run it off the card.
  const rEnd = Math.min(maxR, 3 * m);
  const stream = (side) =>
    `<line class="hz-stream" x1="${X(0)}" y1="${yAt(side, 0).toFixed(1)}" x2="${X(rEnd).toFixed(1)}" y2="${yAt(side, rEnd).toFixed(1)}"/>`;
  let ticks = "";
  for (let r = 100; r <= 600; r += 100) {
    ticks += `<line class="hz-tick" x1="${X(r)}" y1="58" x2="${X(r)}" y2="62"/><text class="hz-lbl" x="${X(r)}" y="70">${r}</text>`;
  }
  const ghost =
    factory > 0 && factory !== m
      ? `<line class="hz-factory" x1="${X(factory)}" y1="${mid - 12}" x2="${X(factory)}" y2="${mid + 12}"/>`
      : "";
  // A blocky top-down fighter at the muzzles, nose to the right.
  const plane =
    `<rect class="hz-plane" x="14" y="${mid - 2}" width="20" height="4"/>` +
    `<rect class="hz-plane" x="22" y="${mid - 16}" width="6" height="32"/>` +
    `<rect class="hz-plane" x="12" y="${mid - 6}" width="3" height="12"/>`;
  return (
    `<svg class="hz-sketch" viewBox="0 0 300 72" role="img" aria-label="wing guns converging at ${m} metres">` +
    `<line class="hz-axis" x1="${x0}" y1="60" x2="${x1}" y2="60"/>${ticks}${ghost}` +
    (lat ? stream(-1) + stream(1) : `<line class="hz-stream" x1="${X(0)}" y1="${mid}" x2="${X(maxR)}" y2="${mid}"/>`) +
    `<circle class="hz-cross" cx="${X(m)}" cy="${mid}" r="5"/>${plane}</svg>`
  );
}

/** Test hook: forget the cached store so a test can seed localStorage fresh. */
export function _resetConvergenceStore() {
  saved = null;
  unsent = null;
}
